import React, { ReactElement, useState } from 'react'
import translate from 'helpers/translate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  FA_CHECK,
  FA_EXCLAMATION_CIRCLE,
  FA_INFO_CIRCLE,
  FA_USER
} from 'const/iconProp'
import { RegistrationUIDErrorType } from 'redux/registration/typings'
import determineValidationIcon from '../determineValidationIcon'
import { RulesContainer, TextfieldWithMargin } from '../subcomponents'
import RuleItem from '../RuleItem'

interface CreateUserIdProps {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFocus: () => void
  validationErrors: RegistrationUIDErrorType
  uniqueUserIdError: string
  name?: string
}

const CreateUserId = ({
  value,
  onChange,
  onBlur,
  validationErrors,
  uniqueUserIdError,
  onFocus,
  name,
  ...props
}: CreateUserIdProps): ReactElement => {
  const [showRulesContainer, setShowRulesContainer] = useState(false)

  const errorKey = Object.keys(validationErrors).find(
    (rule: keyof RegistrationUIDErrorType) => !validationErrors[rule]
  )

  const pristine = Object.keys(validationErrors).find(
    (rule: keyof RegistrationUIDErrorType) =>
      validationErrors[rule] === 'pristine'
  )

  let errorMessageKey = ''

  if (uniqueUserIdError) {
    errorMessageKey = uniqueUserIdError
  } else if (errorKey === 'required') {
    errorMessageKey = 'required'
  } else if (errorKey && errorKey !== 'required') {
    errorMessageKey = 'minSixCharEmail'
  }

  return (
    <>
      <TextfieldWithMargin
        id="registrationUID"
        data-testid="registrationUID"
        name={name || 'registrationUID'}
        type="text"
        value={value}
        onChange={onChange}
        maxLength={50}
        onFocus={() => {
          setShowRulesContainer(true)
          onFocus()
        }}
        onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
          onBlur(event)
          if (!event.target.value) {
            setShowRulesContainer(false)
          }
        }}
        placeholder={translate({ string: 'userId' })}
        error={
          errorMessageKey &&
          translate({
            string: errorMessageKey
          })
        }
        inputClassName="ensightencustomevent"
        data-reason="registrationUID"
        leftIcon={<FontAwesomeIcon icon={FA_USER} />}
        errorIcon={<FontAwesomeIcon icon={FA_EXCLAMATION_CIRCLE} />}
        width="275px"
        required
        {...props}
      />
      <RulesContainer showRulesContainer={showRulesContainer}>
        {showRulesContainer && (errorKey || pristine) ? (
          <>
            {Object.keys(validationErrors).map(rule => {
              const validationRule =
                validationErrors[rule as keyof RegistrationUIDErrorType]
              const validationIcon = determineValidationIcon(validationRule)

              return (
                rule !== 'required' &&
                rule !== 'containsNumber' &&
                rule !== 'emailAddressPermitted' && (
                  <RuleItem
                    rule={rule}
                    key={rule}
                    icon={validationIcon}
                    pristine={validationRule === 'pristine'}
                    hasError={!validationRule}
                  />
                )
              )
            })}
            <RuleItem
              rule="emailAddressPermitted"
              icon={<FontAwesomeIcon icon={FA_INFO_CIRCLE} />}
              pristine
            />
            <RuleItem
              rule="containsNumber"
              icon={<FontAwesomeIcon icon={FA_INFO_CIRCLE} />}
              pristine
            />
          </>
        ) : (
          !errorKey &&
          !pristine &&
          !errorMessageKey && (
            <RuleItem
              rule="readyToGo"
              icon={<FontAwesomeIcon icon={FA_CHECK} />}
            />
          )
        )}
      </RulesContainer>
    </>
  )
}

export default CreateUserId
